import { defineComponent as _defineComponent } from 'vue';
import { unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, mergeProps as _mergeProps, createVNode as _createVNode, withCtx as _withCtx, withKeys as _withKeys, createElementBlock as _createElementBlock } from "vue";
const _hoisted_1 = {
    class: "px-5 pt-6 pb-4 flex flex-col flex-1 min-h-0",
    "data-testid": "project-list-view"
};
const _hoisted_2 = { class: "flex items-start justify-between gap-3 mb-6 sm:mb-8" };
const _hoisted_3 = { class: "flex flex-col gap-1" };
const _hoisted_4 = { class: "text-2xl sm:text-3xl font-bold text-(--colors-text-heading) shrink-0" };
const _hoisted_5 = { class: "text-sm text-(--colors-text-body-subtle)" };
const _hoisted_6 = ["id"];
import { computed, h, onMounted, ref, useId, watch } from "vue";
import { RouterLink, useRoute } from "vue-router";
import { useI18n } from "vue-i18n";
import { Badge, Button, DataTable, Modal, Pager, TableFilterSelector, TableShell, TextInput, useTableQuery, useTableViews, } from "@eudonet/opal";
import { usePermissions } from "@/shared/composables/usePermissions";
import { useProfileStore } from "@/modules/settings/public";
import { formatDate as formatDateWithProfile } from "@/shared/utils/date-formatting";
import { useFinanceFormatters } from "@/shared/composables/useFinanceFormatters";
import { useProjectAmounts } from "../composables/useProjectAmounts";
import { logger } from "@/infrastructure/services/LoggerService";
import { buildTableLabels } from "@/shared/composables/tableLabels";
import { buildFilterSelectorLabels } from "@/shared/composables/filterSelectorLabels";
import { useListSettings } from "@/shared/composables/useListSettings";
import { useTableQueryUrlSync } from "@/shared/composables/useTableQueryUrlSync";
import { fundingProgress } from "../../domain/funding";
import { searchProjects } from "../../infrastructure/api/projectsApi";
import { PROJECT_STATUSES, } from "../../domain/entities/Project";
import { projectStatusTheme } from "../composables/projectStatusBadge";
import { PROJECT_VIEW_PRESETS } from "../composables/projectViewPresets";
import { useProjectViewPreferences } from "../composables/useProjectViewPreferences";
import ProjectCreateModal from "../components/ProjectCreateModal.vue";
export default /*@__PURE__*/ _defineComponent({
    __name: 'ProjectListView',
    setup(__props) {
        const { t, locale } = useI18n();
        const { can } = usePermissions();
        const profileStore = useProfileStore();
        const { formatPercent } = useFinanceFormatters();
        const { formatProjectAmount } = useProjectAmounts();
        const route = useRoute();
        const canCreate = computed(() => can("oproj.edit"));
        const ROW_OPTIONS = [10, 30, 50];
        // ── Table query (search, status filter, pagination) ───────────────────────────
        const statusItems = computed(() => PROJECT_STATUSES.map((status) => ({ id: status, label: t(`projects.status.${status}`) })));
        const tableQuery = useTableQuery({
            pageSize: ROW_OPTIONS[0],
            filterLabels: () => ({ status: t("projects.list.columns.status") }),
            filterItems: () => ({ status: statusItems.value }),
        });
        // ── Preset views (tab strip at Level 1) — "all" + one quick tab per status ────
        const tableViews = useTableViews({
            initialViews: [
                { id: "all", label: t("projects.title"), isPreset: true, isDefault: true, querySnapshot: {} },
                ...PROJECT_VIEW_PRESETS.map((p) => ({
                    id: p.id,
                    label: t(p.labelKey),
                    isPreset: true,
                    querySnapshot: { filters: { [p.filterKey]: { type: "in", values: [p.value] } } },
                })),
            ],
        });
        const tableLabels = computed(() => buildTableLabels(t));
        const filterSelectorLabels = computed(() => buildFilterSelectorLabels(t));
        // When a tab is activated, apply its querySnapshot filters to the shared query state.
        // flush: "sync" lets useTableQueryUrlSync's URL-driven filter overrides (which run
        // immediately after setView) land after the preset filters, not before.
        watch(() => tableQuery.state.activeViewId, (id) => {
            const view = tableViews.views.value.find((v) => v.id === id);
            const nextFilters = view?.querySnapshot?.filters;
            if (nextFilters) {
                // Deep-clone so the live query never shares references with the view's snapshot — otherwise
                // editing a filter would mutate the saved list and silently re-persist it.
                tableQuery.state.filters = JSON.parse(JSON.stringify(nextFilters));
            }
            else if (Object.keys(tableQuery.state.filters).length > 0) {
                tableQuery.clearAllFilters();
            }
            if (tableQuery.state.page !== 1) {
                tableQuery.setPage(1);
            }
        }, { flush: "sync" });
        // ── URL sync (preset view, status filter, search, pagination) ─────────────────
        useTableQueryUrlSync({
            tableQuery,
            syncView: true,
            syncSearch: true,
            searchDebounceMs: 0, // the TableShell search debounces already
            defaultViewId: "all",
            defaultPageSize: ROW_OPTIONS[0],
            filterKeys: {
                status: { paramName: "status" },
            },
        });
        // ── Server-side load (q + status + pagination all ride the request) ───────────
        const rows = ref([]);
        /** A list row: the search item plus the funding rate the "% Fin." column reads (OPAL keys a column on a row property). */
        const tableRows = computed(() => rows.value.map((item) => ({
            ...item,
            fundingPercent: fundingProgress(item.fundedAmount, item.objectiveAmount)?.percent ?? null,
        })));
        const totalItems = ref(0);
        const totalPages = ref(0);
        const loading = ref(false);
        const loadError = ref(false);
        const searchTerm = computed(() => (tableQuery.state.search ?? "").trim());
        // Out-of-order guard: each fetch takes a token, and only the LATEST in-flight call may write
        // the list — a slow earlier response must never overwrite a newer one.
        let fetchSeq = 0;
        function statusesFromQuery() {
            const statusFilter = tableQuery.state.filters["status"];
            return statusFilter?.type === "in" ? statusFilter.values : [];
        }
        async function load() {
            const seq = ++fetchSeq;
            loading.value = true;
            loadError.value = false;
            try {
                const result = await searchProjects(searchTerm.value, tableQuery.state.page, tableQuery.state.pageSize, statusesFromQuery(), { includeFunding: true });
                if (seq !== fetchSeq)
                    return;
                rows.value = result.items;
                totalItems.value = result.totalItems;
                totalPages.value = result.totalPages;
            }
            catch (e) {
                if (seq !== fetchSeq)
                    return;
                loadError.value = true;
                logger.error("Projects list load failed", e);
            }
            finally {
                if (seq === fetchSeq)
                    loading.value = false;
            }
        }
        // Reload on query state change — excludes activeViewId so this only fires AFTER the
        // tab-switch watcher has applied the correct filters. Deep-watching rather than pushing
        // from the handlers: the filter-bar pill's ✕ calls query.removeFilter() directly
        // (TableFilterBar injects the query), so a push-based handler never hears about that path.
        // MUST be registered AFTER useTableQueryUrlSync() above: that composable hydrates URL
        // filters synchronously during its own setup, and a watcher only reacts to mutations that
        // happen after it starts observing — so onMounted's explicit load() stays the single
        // initial request.
        // The search sits in the same watcher as the page: OPAL's `setSearch` resets the page in the
        // same tick, and two watchers would send two requests for one search. A writer that skips
        // `setSearch` leaves the page as is, hence the reset, whose own change then loads.
        watch(() => ({
            filters: tableQuery.state.filters,
            page: tableQuery.state.page,
            pageSize: tableQuery.state.pageSize,
            search: searchTerm.value,
        }), (next, previous) => {
            if (next.search !== previous.search && next.page !== 1)
                tableQuery.setPage(1);
            else
                void load();
        }, { deep: true });
        onMounted(async () => {
            await load();
            // Restore the user's saved prefiltered lists + active tab from OPREF — must run even
            // when the initial load above failed (load() never throws; it flags loadError).
            await restoreViewPreferences();
        });
        // ── "Filtrer" dropdown + clickable filter pills ───────────────────────────────
        const filterSelectorRef = ref(null);
        const filterFields = computed(() => [
            {
                key: "status",
                label: t("projects.list.columns.status"),
                icon: "ri-pulse-line",
                type: "list",
                items: statusItems.value,
                selectAllLabel: t("table.selectAll"),
                step2SearchPlaceholder: t("projects.list.filter.searchValue"),
            },
        ]);
        // 'list' fields emit @apply(key, values); convert to an "in" filter (or clear when empty),
        // and land back on page 1 — the narrowed result set may not reach the current page.
        function handleFilterApply(key, values) {
            if (values.length > 0) {
                tableQuery.setFilter(key, { type: "in", values });
            }
            else {
                tableQuery.removeFilter(key);
            }
            if (tableQuery.state.page !== 1) {
                tableQuery.setPage(1);
            }
        }
        // ── Custom prefiltered lists ("Mes listes") persistence ───────────────────────
        // A list stores the filter state it applies. It can be built by combining selected Eudonet
        // presets + existing lists ("Créer la liste"), or by saving the current filters ("Enregistrer").
        // Lists become tabs, can be renamed / deleted, and are persisted to OPREF (restored next session).
        const { savedViews, createListFromSelection, saveCurrentView, renameList, deleteList, restore: restoreViewPreferences, } = useProjectViewPreferences({
            tableQuery,
            tableViews,
            hasUrlView: () => !!route.query.view,
        });
        // "Enregistrer" (save-view) — persist the current filter state as a new list.
        function onSaveCurrentView() {
            saveCurrentView(tableQuery.state.filters);
        }
        // ── Rename-list modal ─────────────────────────────────────────────────────────
        const renameTitleId = useId();
        const renameModalOpen = ref(false);
        const renameTargetId = ref(null);
        const renameDraft = ref("");
        function openRenameList(id, currentLabel) {
            renameTargetId.value = id;
            renameDraft.value = currentLabel;
            renameModalOpen.value = true;
        }
        function confirmRenameList() {
            if (renameTargetId.value && renameDraft.value.trim()) {
                renameList(renameTargetId.value, renameDraft.value);
            }
            renameModalOpen.value = false;
        }
        // ── tabDropdowns ─────────────────────────────────────────────────────────────
        // Eudonet group = preset filter criteria (one per status). The "Mes listes" group lists the
        // user's created lists (each with a rename / delete ⋯ menu). Checking any combination across
        // BOTH groups and clicking "Créer la liste" creates one new list whose filter merges the
        // selected presets + the selected lists' filters.
        const tabDropdowns = computed(() => {
            const eudonetItems = PROJECT_VIEW_PRESETS.map((p) => ({
                id: p.id,
                label: t(p.labelKey),
                group: "eudonet",
            }));
            const myItems = savedViews.value.map((v) => ({
                id: v.id,
                label: v.label,
                group: "my",
                moreActions: [
                    {
                        label: t("projects.list.views.renameList"),
                        icon: "ri-pencil-line",
                        onClick: () => openRenameList(v.id, v.label),
                    },
                    {
                        label: t("projects.list.views.deleteList"),
                        icon: "ri-delete-bin-line",
                        variant: "danger",
                        onClick: () => deleteList(v.id),
                    },
                ],
            }));
            return [
                {
                    label: t("table.tabs.myPreFilteredLists"),
                    iconLeft: "ri-equalizer-line",
                    showHeader: true,
                    selectedCountSuffix: t("table.tabs.selected"),
                    showSearch: true,
                    searchPlaceholder: t("table.tabs.searchPlaceholder"),
                    searchClearLabel: t("common.clearSearch"),
                    size: "l",
                    emptyLabel: t("table.tabs.noListCreated"),
                    groupButtons: [
                        { value: "eudonet", label: `Eudonet (${eudonetItems.length})` },
                        { value: "my", label: `${t("table.tabs.myLists")} (${myItems.length})` },
                    ],
                    items: [...eudonetItems, ...myItems],
                    footerButtons: [
                        {
                            label: t("table.tabs.createList"),
                            variant: "primary",
                            onClick: (selectedItems) => {
                                createListFromSelection(selectedItems.map((i) => i.id));
                            },
                        },
                    ],
                },
            ];
        });
        // Share ("Partager") stays hidden until view sharing exists.
        const tablePermissions = {
            canShareView: false,
        };
        // ── Column settings (server-persisted via useListSettings) ────────────────────
        // Visibility, order, pinning AND per-column widths persist to OPREF (PUT
        // /list-settings/projects) and restore next session. TableShell hosts the
        // FacetedSearchDrawer from this object (gear → @settings-click) and wires
        // handleSave/handleReset internally. The name column is locked: always visible,
        // pinned left (disabled + pinned, re-asserted on hydration by useListSettings).
        const columnSettings = useListSettings({
            entity: "projects",
            columns: () => [
                { key: "name", label: t("projects.list.columns.name"), visible: true, pinned: true, disabled: true },
                { key: "code", label: t("projects.list.columns.code"), visible: true, pinned: false },
                { key: "status", label: t("projects.list.columns.status"), visible: true, pinned: false },
                // "Financé" is a key-value column of the list, like the name: always shown, never hidden.
                {
                    key: "fundedAmount",
                    label: t("projects.list.columns.funded"),
                    visible: true,
                    pinned: false,
                    disabled: true,
                },
                { key: "objectiveAmount", label: t("projects.list.columns.objective"), visible: true, pinned: false },
                {
                    key: "fundingPercent",
                    label: t("projects.list.columns.fundingPercent"),
                    visible: true,
                    pinned: false,
                },
                { key: "projectType", label: t("projects.list.columns.type"), visible: true, pinned: false },
                { key: "startDate", label: t("projects.list.columns.startDate"), visible: true, pinned: false },
            ],
            defaultOrder: [
                "name",
                "code",
                "status",
                "fundedAmount",
                "objectiveAmount",
                "fundingPercent",
                "projectType",
                "startDate",
            ],
            defaultPinning: { left: ["name"], right: [] },
        });
        const columnPinning = computed(() => ({
            left: columnSettings.pinning.value.left ?? [],
            right: columnSettings.pinning.value.right ?? [],
        }));
        // ── Empty / error state ───────────────────────────────────────────────────────
        const emptyText = computed(() => loadError.value
            ? t("projects.list.loadError")
            : loading.value
                ? t("projects.list.loading")
                : t("projects.list.empty"));
        const formatDate = (date) => date ? formatDateWithProfile(date, locale.value, profileStore.profile?.dateFormat) : "–";
        // ── Create modal — lazy-mounted on first open ─────────────────────────────────
        const showCreateModal = ref(false);
        const createModalMounted = ref(false);
        function openCreateModal() {
            createModalMounted.value = true;
            showCreateModal.value = true;
        }
        // ── Table columns ─────────────────────────────────────────────────────────────
        const columns = computed(() => [
            {
                key: "name",
                header: t("projects.list.columns.name"),
                cell: ({ row }) => h(RouterLink, {
                    to: { name: "ProjectDetail", params: { projectId: row.original.projectId } },
                    class: "text-(--colors-text-link) hover:underline",
                    "data-testid": `project-link-${row.original.projectId}`,
                }, () => row.original.name),
            },
            {
                key: "code",
                header: t("projects.list.columns.code"),
                cell: ({ row }) => row.original.code,
                draggable: true,
            },
            {
                key: "status",
                header: t("projects.list.columns.status"),
                cell: ({ row }) => {
                    const status = row.original.status;
                    return h(Badge, {
                        label: t(`projects.status.${status}`),
                        theme: projectStatusTheme[status] ?? "neutral",
                        showCloseIcon: false,
                        size: "sm",
                    });
                },
                draggable: true,
            },
            {
                key: "fundedAmount",
                header: t("projects.list.columns.funded"),
                // Null is UNKNOWN (the BFF's OPDON leg failed), rendered as the placeholder, never as 0,00.
                cell: ({ row }) => row.original.fundedAmount != null ? formatProjectAmount(row.original.fundedAmount) : "–",
                draggable: true,
            },
            {
                key: "objectiveAmount",
                header: t("projects.list.columns.objective"),
                cell: ({ row }) => row.original.objectiveAmount != null ? formatProjectAmount(row.original.objectiveAmount) : "–",
                draggable: true,
            },
            {
                key: "fundingPercent",
                header: t("projects.list.columns.fundingPercent"),
                cell: ({ row }) => formatPercent(row.original.fundingPercent, 0),
                draggable: true,
            },
            {
                key: "projectType",
                header: t("projects.list.columns.type"),
                cell: ({ row }) => row.original.projectType ? t(`projects.type.${row.original.projectType}`) : "–",
                draggable: true,
            },
            {
                key: "startDate",
                header: t("projects.list.columns.startDate"),
                cell: ({ row }) => formatDate(row.original.startDate),
                draggable: true,
            },
        ]);
        return (_ctx, _cache) => {
            return (_openBlock(), _createElementBlock("div", _hoisted_1, [
                _createElementVNode("div", _hoisted_2, [
                    _createElementVNode("div", _hoisted_3, [
                        _createElementVNode("h1", _hoisted_4, _toDisplayString(_unref(t)("projects.title")), 1 /* TEXT */),
                        _createElementVNode("p", _hoisted_5, _toDisplayString(_unref(t)("projects.subtitle")), 1 /* TEXT */)
                    ]),
                    (canCreate.value)
                        ? (_openBlock(), _createBlock(_unref(Button), {
                            key: 0,
                            variant: "primary",
                            "icon-left": "ri-add-fill",
                            label: _unref(t)('projects.list.createButton'),
                            "data-testid": "project-create-button",
                            onClick: openCreateModal
                        }, null, 8 /* PROPS */, ["label"]))
                        : _createCommentVNode("v-if", true)
                ]),
                _createVNode(_unref(TableShell), {
                    query: _unref(tableQuery),
                    views: _unref(tableViews),
                    labels: tableLabels.value,
                    permissions: tablePermissions,
                    "tab-dropdowns": tabDropdowns.value,
                    "column-settings": _unref(columnSettings),
                    "show-view-selector": true,
                    "search-placeholder": _unref(t)('projects.list.searchPlaceholder'),
                    onSaveView: onSaveCurrentView,
                    onEditFilter: _cache[1] || (_cache[1] = (key) => filterSelectorRef.value?.openForField(key))
                }, {
                    filter: _withCtx(() => [
                        _createVNode(_unref(TableFilterSelector), _mergeProps(filterSelectorLabels.value, {
                            ref_key: "filterSelectorRef",
                            ref: filterSelectorRef,
                            fields: filterFields.value,
                            "active-filters": _unref(tableQuery).state.filters,
                            "trigger-label": _unref(t)('table.filter'),
                            "trigger-icon-left": "ri-filter-2-line",
                            "step1-search-placeholder": _unref(t)('projects.list.filter.searchField'),
                            "confirm-label": _unref(t)('table.tabs.apply'),
                            "back-label": _unref(t)('common.back'),
                            "advanced-filters-label": "",
                            "no-results-label": _unref(t)('projects.list.filter.noResults'),
                            onApply: handleFilterApply,
                            onRemoveFilter: _cache[0] || (_cache[0] = (key) => _unref(tableQuery).removeFilter(key))
                        }), null, 16 /* FULL_PROPS */, ["fields", "active-filters", "trigger-label", "step1-search-placeholder", "confirm-label", "back-label", "no-results-label"])
                    ]),
                    table: _withCtx(() => [
                        _createVNode(_unref(DataTable), {
                            columns: columns.value,
                            data: tableRows.value,
                            "column-visibility": _unref(columnSettings).visibility,
                            "column-order": _unref(columnSettings).order.value,
                            "column-pinning": columnPinning.value,
                            "column-sizing": _unref(columnSettings).widths.value,
                            loading: loading.value,
                            "has-settings": "",
                            "sticky-header": "",
                            "empty-text": emptyText.value,
                            onSettingsClick: _unref(columnSettings).openDrawer,
                            "onUpdate:columnSizing": _unref(columnSettings).handleSizingChange
                        }, null, 8 /* PROPS */, ["columns", "data", "column-visibility", "column-order", "column-pinning", "column-sizing", "loading", "empty-text", "onSettingsClick", "onUpdate:columnSizing"])
                    ]),
                    pagination: _withCtx(() => [
                        _createVNode(_unref(Pager), {
                            "current-page": _unref(tableQuery).state.page,
                            "total-pages": totalPages.value,
                            "total-items": totalItems.value,
                            "items-per-page": _unref(tableQuery).state.pageSize,
                            "rows-per-page-options": ROW_OPTIONS,
                            "rows-per-page-label": _unref(t)('pagination.rowsPerPage'),
                            "items-count-separator": _unref(t)('pagination.of'),
                            "previous-label": _unref(t)('pagination.previous'),
                            "next-label": _unref(t)('pagination.next'),
                            "go-to-page-label": _unref(t)('pagination.goToPage'),
                            "go-label": _unref(t)('pagination.go'),
                            "show-go-to-page": "",
                            onPageChange: _unref(tableQuery).setPage,
                            onItemsPerPageChange: _unref(tableQuery).setPageSize
                        }, null, 8 /* PROPS */, ["current-page", "total-pages", "total-items", "items-per-page", "rows-per-page-label", "items-count-separator", "previous-label", "next-label", "go-to-page-label", "go-label", "onPageChange", "onItemsPerPageChange"])
                    ]),
                    _: 1 /* STABLE */
                }, 8 /* PROPS */, ["query", "views", "labels", "tab-dropdowns", "column-settings", "search-placeholder"]),
                (createModalMounted.value)
                    ? (_openBlock(), _createBlock(ProjectCreateModal, {
                        key: 0,
                        modelValue: showCreateModal.value,
                        "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event) => ((showCreateModal).value = $event)),
                        onCreated: load
                    }, null, 8 /* PROPS */, ["modelValue"]))
                    : _createCommentVNode("v-if", true),
                _createVNode(_unref(Modal), {
                    modelValue: renameModalOpen.value,
                    "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event) => ((renameModalOpen).value = $event)),
                    size: "sm",
                    "aria-labelledby": _unref(renameTitleId)
                }, {
                    header: _withCtx(() => [
                        _createElementVNode("h3", {
                            id: _unref(renameTitleId),
                            class: "text-lg font-bold text-(--colors-text-heading)"
                        }, _toDisplayString(_unref(t)("projects.list.views.renameTitle")), 9 /* TEXT, PROPS */, _hoisted_6)
                    ]),
                    footer: _withCtx(() => [
                        _createVNode(_unref(Button), {
                            variant: "secondary",
                            label: _unref(t)('common.cancel'),
                            onClick: _cache[4] || (_cache[4] = ($event) => (renameModalOpen.value = false))
                        }, null, 8 /* PROPS */, ["label"]),
                        _createVNode(_unref(Button), {
                            variant: "primary",
                            label: _unref(t)('projects.list.views.save'),
                            disabled: !renameDraft.value.trim(),
                            "data-testid": "project-rename-list-confirm",
                            onClick: confirmRenameList
                        }, null, 8 /* PROPS */, ["label", "disabled"])
                    ]),
                    default: _withCtx(() => [
                        _createVNode(_unref(TextInput), {
                            modelValue: renameDraft.value,
                            "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event) => ((renameDraft).value = $event)),
                            label: _unref(t)('projects.list.views.renameLabel'),
                            "data-testid": "project-rename-list-input",
                            "full-width": "",
                            onKeyup: _withKeys(confirmRenameList, ["enter"])
                        }, null, 8 /* PROPS */, ["modelValue", "label"])
                    ]),
                    _: 1 /* STABLE */
                }, 8 /* PROPS */, ["modelValue", "aria-labelledby"])
            ]));
        };
    }
});
